import React from "react";
import { BookOpen } from "lucide-react";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import {
  StandardizedTable,
  type TableColumn,
  type TableRow,
} from "@/components/ui/standardized-table";
import type { TableQueryDTO } from "@/types/table-query";
import type {
  InstagramStoriesApiResponse,
  InstagramStoryRow,
} from "./instagram.types";

interface InstagramStoriesTableProps {
  data: InstagramStoryRow[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
  };
  totalRow?: InstagramStoriesApiResponse["total_row"] | null;
  onQueryChange: (query: TableQueryDTO) => void;
  loading?: boolean;
}

/** Colunas da tabela de stories — as de `GET /api/v1/instagram-stories`. */
const STORIES_COLUMNS: TableColumn[] = [
  { key: "rowNumber", header: "#", align: "left", sortable: false, width: 40 },
  {
    key: "date",
    header: "Data",
    align: "left",
    type: "text",
    sortable: true,
    width: 120,
    maxWidth: 120,
  },
  {
    // A API mantém a chave `impressions`, mas o número é o `views` do Graph — a métrica
    // `impressions` não existe desde a v22 (M-094). É o mesmo nome da tabela de posts.
    key: "impressions",
    header: "Visualizações",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "reach",
    header: "Alcance",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "replies",
    header: "Respostas",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "exits",
    header: "Saídas",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "taps_forward",
    header: "Taps Avançar",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "taps_back",
    header: "Taps Voltar",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "retention_rate",
    header: "Retenção (%)",
    align: "right",
    type: "percent",
    sortable: true,
  },
];

/** Transform API story data to table rows */
function toTableRows(stories: InstagramStoryRow[]): TableRow[] {
  return stories.map((s, i) => ({
    id: s.story_id,
    rowNumber: i + 1,
    date: s.date,
    impressions: s.impressions,
    reach: s.reach,
    replies: s.replies,
    exits: s.exits,
    taps_forward: s.taps_forward,
    taps_back: s.taps_back,
    retention_rate: s.retention_rate,
  }));
}

function mapTotalRow(
  totalRow?: InstagramStoriesApiResponse["total_row"] | null,
): Record<string, string | number> | undefined {
  if (!totalRow) return undefined;

  return {
    rowNumber: "",
    date: "Total Geral",
    impressions: Number(totalRow.impressions || 0),
    reach: Number(totalRow.reach || 0),
    replies: Number(totalRow.replies || 0),
    exits: Number(totalRow.exits || 0),
    taps_forward: Number(totalRow.taps_forward || 0),
    taps_back: Number(totalRow.taps_back || 0),
    retention_rate:
      typeof totalRow.retention_rate === "number"
        ? totalRow.retention_rate
        : "-",
  };
}

export default function InstagramStoriesTable({
  data,
  pagination,
  totalRow,
  onQueryChange,
  loading = false,
}: InstagramStoriesTableProps): React.ReactElement {
  // A tabela fica sempre montada: o carregamento e o vazio são dela. Trocá-la por um card
  // levava junto a caixa de busca, e uma busca sem resultado não tinha como ser desfeita.
  return (
    <StandardizedTable
      title={
        <span className="inline-flex items-center gap-2">
          <BookOpen className="h-4 w-4" />
          Stories
          <HelpTooltip
            content="Cada story é registrado enquanto está no ar (24 horas). A linha de total cobre o período inteiro, inclusive stories anteriores ao início do registro."
            triggerClassName="h-3.5 w-3.5 text-[9px]"
          />
        </span>
      }
      columns={STORIES_COLUMNS}
      data={toTableRows(data)}
      total={mapTotalRow(totalRow)}
      queryMode="server"
      onQueryChange={onQueryChange}
      pagination={pagination}
      loading={loading}
      mensagemVazia="Nenhum story encontrado no período selecionado."
    />
  );
}
